import NProgress from 'nprogress'
import { useEffect } from 'react'
// @mui
import { GlobalStyles } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// ----------------------------------------------------------------------

NProgress.configure({
  showSpinner: false,
  // A route whose chunk is already cached lands in a few frames, and a bar that
  // flashed for one of them would read as a glitch rather than as progress.
  trickleSpeed: 120,
})

export function ProgressBarStyle() {
  const theme = useTheme()

  return (
    <GlobalStyles
      styles={{
        '#nprogress': {
          'pointerEvents': 'none',
          '& .bar': {
            top: 0,
            left: 0,
            height: 2,
            width: '100%',
            position: 'fixed',
            zIndex: theme.zIndex.snackbar,
            backgroundColor: theme.palette.primary.main,
            boxShadow: `0 0 2px ${theme.palette.primary.main}`,
          },
          '& .peg': {
            right: 0,
            opacity: 1,
            width: 100,
            height: '100%',
            display: 'block',
            position: 'absolute',
            transform: 'rotate(3deg) translate(0px, -4px)',
            boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`,
          },
        },
      }}
    />
  )
}

// ----------------------------------------------------------------------
// One bar, however many things are waiting.
//
// NProgress is a module singleton, so the count of what is waiting has to live
// at the same scope. A route change mounts two loading states in a row — the
// chunk, then the screen's own query — and without the count the first one's
// cleanup would run the bar to full and the second would restart it from zero,
// in the same commit. The handover is deferred by a tick for exactly that: a
// loading state replaced by another loading state is one wait, not two.
// ----------------------------------------------------------------------

let waiting = 0
let finishing: ReturnType<typeof setTimeout> | null = null

/**
 * Whether the bar is on screen right now.
 *
 * React flushes every passive cleanup before every passive setup, so a route
 * change really does take the count to zero and back to one inside one commit.
 * `NProgress.start()` on the way back would begin a second trickle timer on top
 * of the first, and each of those recurses forever, so a session of clicking
 * around accumulates one per navigation. The count says how many things are
 * waiting; this says whether the bar has already been started for them.
 */
let running = false

const beginWaiting = () => {
  if (finishing) {
    clearTimeout(finishing)
    finishing = null
  }
  waiting += 1
  if (!running) {
    running = true
    NProgress.start()
  }
}

const endWaiting = () => {
  waiting -= 1
  if (waiting > 0) return

  // Deferred, because a loading state replaced by another loading state is one
  // wait: finishing here would run the bar to full and the next one would
  // restart it from zero, in the same commit.
  finishing = setTimeout(() => {
    finishing = null
    if (waiting === 0 && running) {
      running = false
      NProgress.done()
    }
  }, 0)
}

/**
 * The thin bar at the top of the window, for as long as something is loading.
 *
 * It runs from mount to unmount, which is the whole point: it is rendered by a
 * loading state, so its lifetime is the wait. It used to start during render
 * and finish in a mount effect, so it completed in the same commit and was gone
 * before the chunk or the query landed — the one affordance that could have
 * carried a wait signalled that the wait was over.
 */
export default function ProgressBar() {
  useEffect(() => {
    beginWaiting()
    return endWaiting
  }, [])

  return null
}
